/**
 * Code Security API module (Code Security, AI Triage, Supply Chain, SCA,
 * SBOM, GitOps, CI/CD Scanner).
 *
 * Real per-domain split from services/api.ts. Verbatim extraction of the 7
 * code-security API objects; behavior is unchanged. Other adjacent exports
 * (e.g. eventStreamApi) remain in api.ts and are re-exported by
 * services/api/index.ts.
 */
import { _unwrap, _thenUnwrap } from './_unwrap';
// `apiClient` is a NAMED export from ./client (verified against vulnerability/ai/easm
// sibling splits). The earlier default-import form would fail at runtime.
import { apiClient as fetchClient } from './client';

// ============================================================================
// CI/CD Scanner API (port 8115) — moved from CSPM to Application Security
// ============================================================================

export const cicdScannerApi = {
  scan: async (data: { pipeline_id: string; artifacts: any[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cicd/scan', data));
  },

  getScans: async (params?: { page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cicd/scans', params));
  },

  getScan: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cicd/scans/${id}`));
  },

  getGatePolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cicd/gate-policies'));
  },

  createGatePolicy: async (data: { name: string; rules: any[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cicd/gate-policies', data));
  },

  getSARIF: async (scanId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cicd/scans/${scanId}/sarif`));
  },
};

// R219 — CI/CD pipeline registration CRUD (Wiz Code → Pipelines).
// BE: services/platform/ci-cd-integration/app/routers/pipelines.py
// Gateway: /api/v1/pipelines → CICD_URL (8115) per service_registry.py:433.
// Companion to cicdScannerApi above (which is /api/v1/cicd/*).
export const pipelinesApi = {
  list: async (params?: { provider?: string; status?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/pipelines', params));
  },
  get: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/pipelines/${id}`));
  },
  create: async (data: {
    name: string;
    repository: string;
    provider: string;
    branch_pattern?: string;
    scan_types?: string[];
    gate_policy_ids?: string[];
    auto_scan_on_pr?: boolean;
    auto_scan_on_push?: boolean;
    auto_pr_comment?: boolean;
    notify_channels?: string[];
    environment?: string;
    metadata?: Record<string, unknown>;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/pipelines', data));
  },
  update: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/pipelines/${id}`, data));
  },
  delete: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/pipelines/${id}`));
  },
  triggerScan: async (id: string, data: { branch?: string; commit_sha?: string; pr_number?: number; scan_types?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/pipelines/${id}/scan`, data));
  },
  listScans: async (id: string, params?: { page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/pipelines/${id}/scans`, params));
  },
  getScan: async (pipelineId: string, scanId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/pipelines/${pipelineId}/scans/${scanId}`));
  },
};

// ── Supply Chain & SBOM API ───────────────────────────────────────────────────
export const supplyChainApi = {
  getStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/supply-chain/statistics'));
  },
  getSboms: async (params?: { target_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/supply-chain/sboms', params));
  },
  getSbom: async (sbomId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/supply-chain/sboms/${sbomId}`));
  },
  exportCycloneDX: async (sbomId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/supply-chain/sboms/${sbomId}/cyclonedx`));
  },
  getLicenseViolations: async (params?: { risk?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/supply-chain/license-violations', params));
  },
  getAlerts: async (params?: { risk_type?: string; severity?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/supply-chain/alerts', params));
  },
  getVulnerablePackages: async (minCves?: number): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/supply-chain/vulnerable-packages', { min_cves: minCves }));
  },
  triggerScan: async (target: string, targetType?: string): Promise<any> => {
    // BE ScanRequest reads `target` from the JSON BODY (required, no default);
    // sending it as a query param left body=null → 422 before the handler ran.
    return _unwrap(await fetchClient.post('/api/v1/supply-chain/scan', { target, target_type: targetType || 'repository' }));
  },
};

// ── SCA (Software Composition Analysis) — Transitive Dependency Scanning ─────
export const scaApi = {
  analyzeDependencies: async (data: {
    project_id?: string;
    project_name?: string;
    manifest_filename: string;
    manifest_content: string;
    lock_filename?: string;
    lock_content?: string;
    project_license?: string;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/sca/analyze-dependencies', data));
  },
  getDependencyTree: async (projectId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/sca/dependency-tree/${projectId}`));
  },
  listAnalyses: async (limit?: number): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/sca/analyses', { limit }));
  },
};

// ── Code Security API ─────────────────────────────────────────────────────────
export const codeSecurityApi = {
  getOverview: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/code-security/overview'));
  },
  getRepos: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/code-security/repos'));
  },
  getRepo: async (repoId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/code-security/repos/${repoId}`));
  },
  addRepo: async (data: { url: string; branch?: string; token?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/code-security/repos', data));
  },
  scanRepo: async (repoId: string, scanTypes?: string[]): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/code-security/repos/${repoId}/scan`, { scan_types: scanTypes }));
  },
  getScan: async (scanId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/code-security/scans/${scanId}`));
  },
  getFindings: async (params?: { severity?: string; category?: string; repo_id?: string; status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/code-security/findings', params as any));
  },
  getRepoFindings: async (repoId: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>(`/api/v1/code-security/repos/${repoId}/findings`));
  },
  createFixPR: async (data: { finding_id: string; repo_url: string; file_path: string; fix_code: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/code-security/fix-pr', data));
  },
  getCodeToCloudMappings: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/code-security/code-to-cloud-mappings'));
  },
  // Wiz-parity License Compliance — backend has /api/v1/code-security/license/*
  // (verified at code-security:8127 main.py:3027, 3107, 3121). Round-7 audit
  // surfaced this as a missing FE tab despite full backend support.
  getLicenseStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/code-security/license/stats'));
  },
  getLicenseViolations: async (params?: { risk?: string; license?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/code-security/license/violations', params as any));
  },
  getLicensePolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/code-security/license/policies'));
  },
};

// ── SBOM API ─────────────────────────────────────────────────────────────────
export const sbomApi = {
  upload: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/sbom/upload', data));
  },
  list: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/sbom'));
  },
  get: async (sbomId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/sbom/${sbomId}`));
  },
  getVulnerabilities: async (sbomId: string, minSeverity?: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/sbom/${sbomId}/vulnerabilities`, { min_severity: minSeverity }));
  },
  generate: async (data: { asset_id: string; format?: 'cyclonedx' | 'spdx' }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/sbom/generate', data));
  },
  analyze: async (sbomId: string): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/sbom/analyze', { sbom_id: sbomId }));
  },
  export: async (params?: { sbom_id?: string; format?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/sbom/export', params));
  },
  getSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/sbom/summary'));
  },
  getComponents: async (params?: { ecosystem?: string; vulnerable_only?: boolean; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/sbom/components', params));
  },
};

// ── GitOps API ──
export const gitopsApi = {
  createConfig: (data: any) => fetchClient.post('/api/v1/policies/gitops/configs', data).then(_thenUnwrap),
  listConfigs: () => fetchClient.get('/api/v1/policies/gitops/configs').then(_thenUnwrap),
  updateConfig: (id: string, data: any) => fetchClient.put(`/api/v1/policies/gitops/configs/${id}`, data).then(_thenUnwrap),
  deleteConfig: (id: string) => fetchClient.delete(`/api/v1/policies/gitops/configs/${id}`).then(_thenUnwrap),
  triggerSync: (id: string) => fetchClient.post(`/api/v1/policies/gitops/sync/${id}`).then(_thenUnwrap),
  getSyncStatus: (id: string) => fetchClient.get(`/api/v1/policies/gitops/sync-status/${id}`).then(_thenUnwrap),
  exportPolicies: () => fetchClient.post('/api/v1/policies/gitops/export').then(_thenUnwrap),
  importPolicies: (data: any) => fetchClient.post('/api/v1/policies/gitops/import', data).then(_thenUnwrap),
};

// ============================================================================
// Code Security AI Triage + Remediation
// ============================================================================

export const codeSecurityTriageApi = {
  // Analysis
  analyze: (data: { finding_id: string; code_context?: string; language?: string }) =>
    fetchClient.post('/api/v1/code-security/ai-triage/analyze', data).then(_thenUnwrap),
  getResults: (params?: { severity?: string; status?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/code-security/ai-triage/results', params).then(_thenUnwrap),
  getResultDetail: (resultId: string) =>
    fetchClient.get(`/api/v1/code-security/ai-triage/results/${resultId}`).then(_thenUnwrap),
  batchAnalyze: (data: { finding_ids: string[]; language?: string }) =>
    fetchClient.post('/api/v1/code-security/ai-triage/batch', data).then(_thenUnwrap),
  getStats: () =>
    fetchClient.get('/api/v1/code-security/ai-triage/stats').then(_thenUnwrap),

  // Fix Generation
  generateFix: (data: { finding_id: string; result_id?: string; language?: string; framework?: string }) =>
    fetchClient.post('/api/v1/code-security/ai-remediation/generate-fix', data).then(_thenUnwrap),
  getFixes: (params?: { finding_id?: string; status?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/code-security/ai-remediation/fixes', params).then(_thenUnwrap),
  getFixDetail: (fixId: string) =>
    fetchClient.get(`/api/v1/code-security/ai-remediation/fixes/${fixId}`).then(_thenUnwrap),
  postToPr: (fixId: string, data: { repo: string; branch: string; title?: string }) =>
    fetchClient.post('/api/v1/code-security/ai-remediation/post-to-pr', data).then(_thenUnwrap),
  getTemplates: (params?: { language?: string; vulnerability_type?: string }) =>
    fetchClient.get('/api/v1/code-security/ai-remediation/templates', params).then(_thenUnwrap),
  getRemediationStats: () =>
    fetchClient.get('/api/v1/code-security/ai-remediation/stats').then(_thenUnwrap),
};


// ============================================================================
// IaC Scanning API (Round 30 — Wiz "Wiz Code" parity)
// ============================================================================
// Backend at `services/platform/code-security/app/iac_router.py`:
//   POST /api/v1/iac/ingest       - upload Terraform/CloudFormation parse tree
//   GET  /api/v1/iac/resources    - list parsed IaC resources (file:line +
//                                    cloud_mapped_resource_id when applicable)
//   GET  /api/v1/iac/findings     - list rule misconfigurations from those
//                                    parsed resources, joined with repo info
// Pre-Round 30 the FE CodeSecurity dashboard had an "IaC Scanning" tab but
// only called the generic `/code-security/vulnerabilities` aggregator — the
// dedicated `csp_iac.terraform_resources` data + file/line + cloud-resource
// mapping were never surfaced. This api object wires the real endpoints.

export interface IaCResource {
  id: string;
  repo_id: string;
  file_path: string;
  tool: string;                          // 'terraform' | 'cloudformation' | ...
  resource_type: string;                 // 'aws_s3_bucket', 'aws_iam_role', ...
  resource_name: string;
  line_number: number | null;
  mapped_cloud_resource_id: string | null;
  commit_sha: string | null;
  commit_author: string | null;
  commit_date: string | null;
  updated_at: string;
}

export interface IaCFinding {
  id: string;
  rule_id: string;
  severity: string;                      // 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
  title: string;
  description: string;
  remediation: string;
  file_path: string;
  line_number: number | null;
  status: string;
  created_at: string;
  repo_name: string | null;
  resource_type: string | null;
  resource_name: string | null;
  mapped_cloud_resource_id: string | null;
}

export interface IaCGraphNode {
  id: string;
  type: 'iac' | 'cloud';
  label: string;
  data: Record<string, any>;
}
export interface IaCGraphEdge {
  id: string;
  source: string;
  target: string;
  data?: Record<string, any>;
}

export const iacApi = {
  listResources: async (params?: {
    repo_id?: string;
    tool?: string;
    resource_type?: string;
    mapped_only?: boolean;
    limit?: number;
  }): Promise<{ items: IaCResource[]; total: number; error?: string }> =>
    _unwrap(await fetchClient.get('/api/v1/iac/resources', params)),

  listFindings: async (params?: {
    severity?: string;
    status?: string;
    limit?: number;
  }): Promise<{ items: IaCFinding[]; total: number; error?: string }> =>
    _unwrap(await fetchClient.get('/api/v1/iac/findings', params)),

  /** Round 34 (2026-05-17): Wiz-Code parity IaC→Cloud graph.
   * Reads csp_graph.edges WHERE type='DEFINED_IN' (already written by
   * sync_iac_graph_edges mig 119) and returns reactflow-ready
   * {nodes:[], edges:[]}. */
  getGraph: async (params?: { limit?: number; repo_id?: string }):
    Promise<{ nodes: IaCGraphNode[]; edges: IaCGraphEdge[]; total_edges: number; has_more: boolean; error?: string }> =>
    _unwrap(await fetchClient.get('/api/v1/graph/iac-graph', params)),
};
